import { GetInvoiceDetailResponseItemDto } from '@ekt-group/general-purpose-api-interfaces';
import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { utils, writeFile } from 'xlsx';

import excelIcon from '@/assets/file-icons/excel.png';
import { DATE_FORMAT } from '@/constants';
import { useGetInvoiceDetails } from '@/features/invoices/api/use-get-invoice-details';

interface DownloadInvoiceDetailsButtonProps {
  invoiceNo: string;
}

interface RowData {
  date: string;
  name: string;
  garbage: string;
  amount: string;
  price: string;
  total: string;
  address: string;
}

interface Column {
  header: string;
  key: string;
}

const DownloadInvoiceDetailsExcelButton = ({ invoiceNo }: DownloadInvoiceDetailsButtonProps) => {
  const { t } = useTranslation('invoicesPage');
  const invoiceDetailsQuery = useGetInvoiceDetails({
    params: { invoiceNo },
    queryConfig: { enabled: false },
  });

  const handleDownload = async () => {
    const { data } = await invoiceDetailsQuery.refetch();
    if (!data) return;

    const wb = utils.book_new();
    const ws = utils.aoa_to_sheet([[]]);

    const columns: Column[] = [
      {
        header: t('date'),
        key: 'date',
      },
      {
        header: t('name'),
        key: 'name',
      },
      {
        header: t('garbageName', { ns: 'objectItemsTable' }),
        key: 'garbage',
      },
      {
        header: t('amount'),
        key: 'amount',
      },
      {
        header: t('price'),
        key: 'price',
      },
      {
        header: t('total'),
        key: 'total',
      },
      {
        header: t('address', { ns: 'contractsTable' }),
        key: 'address',
      },
    ];

    utils.sheet_add_aoa(ws, [columns.map((col) => col.header)], { origin: 'A1' });

    const allRows = data.flatMap((detail: GetInvoiceDetailResponseItemDto) => {
      return detail.rows.map((row: any): RowData => ({
        ...row,
        date: dayjs(row.date).format(DATE_FORMAT),
        amount: row.amount.replace('.', ','),
        total: row.total.replace('.', ','),
        price: row.price.replace('.', ','),
        address: detail.address,
      }));
    });

    const dataWithCustomColumns = allRows.map((item: RowData) => {
      const newItem: Record<string, string> = {};
      columns.forEach(({ header, key }) => {
        newItem[header] = item[key as keyof RowData];
      });
      return newItem;
    });

    utils.sheet_add_json(ws, dataWithCustomColumns, {
      origin: { r: 1, c: 0 },
      skipHeader: true,
    });

    utils.book_append_sheet(wb, ws, 'Invoice Details');
    writeFile(wb, `${invoiceNo}-details.xlsx`);
  };

  if (invoiceDetailsQuery.isFetching) {
    return <Skeleton.Node active className="w-6 h-6" />;
  }

  const renderButtonContent = () => {
    if (invoiceDetailsQuery.isFetching) {
      return <Skeleton.Node active className="w-6 h-6" />;
    }
    return <img src={excelIcon} alt="excel" width={24} height={24} />;
  };

  return (
    <button onClick={handleDownload} disabled={invoiceDetailsQuery.isFetching}>
      {renderButtonContent()}
    </button>
  );
};

export default DownloadInvoiceDetailsExcelButton;
